import axios from "axios";
import {makeField} from "js/UI/forms";
import * as _ from "lodash";
import {Field, Form} from "@/Components/forms";

const DEBUG_MODE = false;

export class UserFormData {
    partner_building_id = makeField('partner_building_id', 'Select your address')
        .addRules('required');

    dormitory = makeField('dormitory', 'Tower / Building / Dormitory')
        .addRules('required', {max: {length: 30}});

    house = makeField('house', 'House')
        .addRules('required', {max: {length: 30}});

    room = makeField('room', 'Room #')
        .addRules('required', {max: {length: 30}});

    student_id = makeField('student_id', 'Student ID')
        .addRules('required', {max: {length: 8}});

    first_name = makeField('first_name', 'Student First Name')
        .addRules('required', {max: {length: 30}});

    last_name = makeField('last_name', 'Student Last Name')
        .addRules('required', {max: {length: 30}});

    phone = makeField('phone', 'Phone')
        .addRules('required', {max: {length: 30}});

    student_email = makeField('student_email', 'Student Email (for validation)')
        .addRules('required', 'email', 'student_email');

    account_email = makeField('account_email', 'Account Email (for login and communication)')
        .addRules('required', 'email');

    special_status = makeField('special_status', 'Fall Return Plans', 'normal_return')
        .setItems([
            {
                text: "I am returning during normal fall move in",
                value: "normal_return"
            },
            {
                text: "R/A",
                value: "ra"
            },
            {
                text: "Storing Summer & Fall Session",
                value: "extended_storage"
            }
        ]);

    moving_off_campus = makeField('moving_off_campus', 'Select if you are moving off campus next semester.');

    password = makeField('password', 'Enter Password')
        .addRules('required', {password: {target: "@password_confirmation"}});

    password_confirmation = makeField('password_confirmation', 'Confirm Password')
        .addRules('required');
}

export class SchedulingFormData {
    drop_off_date = makeField('drop_off_date', '- Select -')
        .addRules('required');

    pick_up_date = makeField('pick_up_date', '- Select -')
        .addRules('required');
}

export class OrderPositionsFormData {
    base_product = makeField("base_product_id", "Base Product ID", null);
    package_addon = makeField("package_addon_id", "Package Addon", null);
    vault_items = makeField("vault_items_count", "Vault Items", 1);
    additional_bags = makeField("additional_bags_count", "Additional Bags", 0);
    mattress_topper = makeField("mattress_topper_id", "Mattress Topper", null);
    declined_bags = makeField("declined_bags", "Declined Bags", null);

    asFields(): Field[] {
        return [
            this.base_product,
            this.package_addon,
            this.vault_items,
            this.additional_bags,
            this.mattress_topper,
            this.declined_bags,
        ]
    }
}

export class CheckoutInfo {
    has_discount = false;
    discount_amount = 0;
    subtotal = 0;
    delivery = 0;
    extras = 0;
    total = 0;
    total_today = 0;
}

export enum PaymentStrategy {
    ONE_TIME = "one_time",
    INSTALLMENT = "installment",
}

export class CheckoutFormData {
    payment = {
        token: null,
        method: null,
    };
    payment_strategy = PaymentStrategy.ONE_TIME;
    promo_code = makeField("promo_code", "Promo Code");
    send_me_deals = true;
}

export interface IUserInfoForm {
    partner_building_id: number | string;
    dormitory: string
    house: string
    room: string
    student_id: string
    first_name: string
    last_name: string
    phone: string
    student_email: string
    account_email: string
}

export interface IUserPaymentCard {
    uuid: string
    card_brand: string
    card_last_four_digits: string
}

export const collegeStorage = {
    namespaced: true,
    state: {
        debugMode: DEBUG_MODE,

        featureFlags: {
            useFullFieldset: false
        },

        userFormData: new UserFormData(),
        orderPositions: new OrderPositionsFormData(),
        schedulingFormData: new SchedulingFormData(),
        checkoutFormData: new CheckoutFormData(),

        checkoutInvoiceInfo: new CheckoutInfo(),

        orderItems: [],
        orderItemsState: null,
        orderItemsError: null,

        promoCode: {
            has_promo_code: false,
            is_ok: false,
            message: null
        },

        availablePartnerBuildings: [],
        is_authenticated: false,
        user_data: null,
        signupFormData: null as any as IUserInfoForm,

        address: null,

        termsAndConditionsPageUrl: '/student-vault-terms',

        isUserInfoFormPrefilled: false,

        userCards: [] as IUserPaymentCard[]
    },
    mutations: {
        loadSandbox(state) {
            Form.fillValues(Object.values(state.userFormData), {
                partner_building_id: 147,
                dormitory: '1A',
                house: '10',
                room: '441',
                student_id: '1A2B3C4D',
                first_name: 'John',
                last_name: 'Doe',
                phone: '0',
                student_email: 'test@uchicago.edu',
                account_email: 'john@example.com',
                password: '1234',
                password_confirmation: '1234',
            });

            Form.fillValues(state.orderPositions.asFields(), {
                package_addon_id: "student-vault-2023-extra-5-items",
                vault_items_count: 2,
                additional_bags_count: 1,
                mattress_topper_id: "student-vault-2023-mattress-topper-premium",
            });
        },
    },
    actions: {
        async loadPageData(context) {
            return axios.get('/uchicago/page-data')
                .then((response) => {
                    context.state.availablePartnerBuildings = response.data.available_partner_buildings;
                    context.state.is_authenticated = response.data.is_authenticated;
                    context.state.user_data = response.data.user_data;
                    context.state.signupFormData = response.data.signupFormData;

                    context.state.isUserInfoFormPrefilled = context.state.signupFormData !== null;

                    if (context.state.signupFormData) {
                        Form.fillValues(Object.values(context.state.userFormData), context.state.signupFormData);
                    }

                    // ???
                    context.state.userFormData.partner_building_id.setItems(response.data.available_partner_buildings);

                    context.state.userFormData.first_name.setValue(context.state.user_data.first_name);
                    context.state.userFormData.last_name.setValue(context.state.user_data.last_name);

                    if (context.state.is_authenticated) {
                        context.state.userFormData.first_name.clearRules();
                        context.state.userFormData.last_name.clearRules();
                        context.state.userFormData.account_email.clearRules();
                        context.state.userFormData.password.clearRules();
                        context.state.userFormData.password_confirmation.clearRules();
                    }
                });
        },

        async loadDeliveryDates(context, partnerBuildingId) {
            return axios.get(`/web-api/partner-buildings/${partnerBuildingId}/delivery-dates`)
                .then((response) => {
                    context.state.schedulingFormData.drop_off_date.setItems(
                        response.data.available_dropoff_dates
                    );

                    context.state.schedulingFormData.pick_up_date.setItems(
                        response.data.available_pickup_dates
                    );

                    context.state.address = response.data.address;
                });
        },

        async loadUserPaymentCards(context) {
            return axios.get(`/web-api/users/me/cards`)
                .then((response) => {
                    context.state.userCards = response.data.data;
                })
                .catch((error) => {
                    console.debug(error);

                    context.state.userCards = [];
                });
        },

        async refreshCheckout(context) {
            context.state.orderItemsState = 'loading';
            context.state.orderItemsError = null;

            console.debug(Form.dumpValues(context.state.orderPositions.asFields()));

            axios.post('/uchicago/summary', {
                items: Form.dumpValues(context.state.orderPositions.asFields()),
                options: {
                    promo_code: context.state.checkoutFormData.promo_code.value,
                    payment_strategy: context.state.checkoutFormData.payment_strategy,
                }
            })
                .then((response) => {
                    console.debug("Got a response", response.data);

                    context.state.orderItems = response.data.items;
                    context.state.checkoutInvoiceInfo = Object.assign(context.state.checkoutInvoiceInfo, response.data.summary);

                    context.state.promoCode.has_promo_code = response.data.promo_code.has_promo_code;
                    context.state.promoCode.is_ok = response.data.promo_code.is_ok;
                    context.state.promoCode.message = response.data.promo_code.message;
                })
                .catch((error) => {
                    console.error(error);

                    context.state.orderItems = [];

                    if (error.status === 422)
                        context.state.orderItemsError = error.data.message;
                    else
                        context.state.orderItemsError = "Internal server error. Please try again later...";
                })
                .finally(() => {
                    context.state.orderItemsState = null;
                });
        },

        submitOrder(context) {
            let data = Object.assign({}, {
                ...context.state.checkoutFormData,
                options: {
                    payment_strategy: context.state.checkoutFormData.payment_strategy,
                    promo_code: context.state.checkoutFormData.promo_code.value,
                    send_me_deals: context.state.checkoutFormData.send_me_deals,
                },
                partner_building_id: context.state.userFormData.partner_building_id.value,
                special_status: context.state.featureFlags.useFullFieldset
                    ? context.state.userFormData.special_status.value
                    : null,
                items: Form.dumpValues(context.state.orderPositions.asFields()),
                user: _.reduce(context.state.userFormData, (accumulator, item) => {
                    accumulator[item.name] = item.value;

                    return accumulator;
                }, {}),
                scheduling: _.reduce(context.state.schedulingFormData, (accumulator, item) => {
                    accumulator[item.name] = item.value;

                    return accumulator;
                }, {}),
            });

            console.debug("Data to submit:", data);

            return new Promise((resolve, reject) => {
                axios.post('/uchicago/order', data)
                    .then((response) => {
                        console.info("Got a response:", response);

                        resolve(response.data);
                    })
                    .catch((error) => {
                        console.error(error);

                        reject(error);
                    });
            });
        }
    },
    getters: {
        userDataFields(state) {
            let fields = [
                {label: "Name", value: `${state.userFormData.first_name.value} ${state.userFormData.last_name.value}`},
                {label: "Phone", value: state.userFormData.phone.value ?? '-'},
                {label: "Student ID", value: state.userFormData.student_id.value ?? '-'},
                {label: "Student Email", value: state.userFormData.student_email.value ?? '-'},
                {label: "Personal Email", value: state.userFormData.account_email.value},
                {label: "Address", value: state.address ?? '-'},
                {label: "Dormitory", value: state.userFormData.dormitory.value ?? '-'},
                {label: "House", value: state.userFormData.house.value ?? '-'},
                {label: "Room", value: state.userFormData.room.value},
            ];

            return fields;
        },
    }
};
